import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { paymentsApi, ApiError, type Wallet } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

declare global {
  interface Window {
    PaystackPop?: { setup: (opts: Record<string, unknown>) => { openIframe: () => void } };
    Stripe?: (key: string) => StripeClient;
  }
}

interface StripeClient {
  elements: () => { create: (type: string) => StripeElement };
  confirmCardPayment: (clientSecret: string, opts: { payment_method: { card: StripeElement } }) => Promise<{ error?: { message: string }; paymentIntent?: { id: string; status: string } }>;
}
interface StripeElement {
  mount: (el: HTMLElement) => void;
  unmount: () => void;
}

const PAYSTACK_PUBLIC_KEY = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY as string | undefined;
const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY as string | undefined;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  token?: string | null;
  onSuccess: (wallet: Wallet) => void;
}

export function TopupDialog({ open, onOpenChange, token, onSuccess }: Props) {
  const [amount, setAmount] = useState("");
  const [provider, setProvider] = useState<"paystack" | "stripe" | null>(null);
  const [processing, setProcessing] = useState(false);
  const cardElRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<{ stripe: StripeClient; card: StripeElement; clientSecret: string } | null>(null);
  const [stripeReady, setStripeReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setAmount("");
      setProvider(null);
      setStripeReady(false);
      stripeRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (stripeReady && cardElRef.current && stripeRef.current) {
      stripeRef.current.card.mount(cardElRef.current);
    }
  }, [stripeReady]);

  const amountKobo = Math.round(parseFloat(amount || "0") * 100);
  const amountValid = Number.isFinite(amountKobo) && amountKobo >= 10000;

  const payWithPaystack = async () => {
    if (!amountValid) return toast.error("Minimum top-up is ₦100");
    if (!PAYSTACK_PUBLIC_KEY) return toast.error("Paystack isn't configured for this environment");
    setProcessing(true);
    try {
      await loadScript("https://js.paystack.co/v1/inline.js");
      const { email, reference } = await paymentsApi.initPaystack(amountKobo, token);
      const handler = window.PaystackPop?.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email,
        amount: amountKobo,
        currency: "NGN",
        ref: reference,
        callback: function () {
          (async () => {
            try {
              const wallet = await paymentsApi.verifyPaystack(reference, token);
              toast.success("Wallet topped up");
              onSuccess(wallet);
              onOpenChange(false);
            } catch (err) {
              toast.error(err instanceof ApiError ? err.message : "Could not verify payment");
            } finally {
              setProcessing(false);
            }
          })();
        },
        onClose: () => {
          setProcessing(false);
          toast.error("Payment canceled");
        },
      });
      handler?.openIframe();
    } catch (err) {
      setProcessing(false);
      console.error("Paystack top-up failed:", err);
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Could not start payment");
    }
  };

  const startStripe = async () => {
    if (!amountValid) return toast.error("Minimum top-up is ₦100");
    if (!STRIPE_PUBLIC_KEY) return toast.error("Stripe isn't configured for this environment");
    setProvider("stripe");
    setProcessing(true);
    try {
      await loadScript("https://js.stripe.com/v3/");
      const { client_secret } = await paymentsApi.initStripe(amountKobo, token);
      const stripe = window.Stripe?.(STRIPE_PUBLIC_KEY);
      if (!stripe) throw new Error("Stripe failed to load");
      const card = stripe.elements().create("card");
      stripeRef.current = { stripe, card, clientSecret: client_secret };
      setStripeReady(true);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not start Stripe payment");
    } finally {
      setProcessing(false);
    }
  };

  const confirmStripe = async () => {
    const ctx = stripeRef.current;
    if (!ctx) return;
    setProcessing(true);
    try {
      const { error, paymentIntent } = await ctx.stripe.confirmCardPayment(ctx.clientSecret, { payment_method: { card: ctx.card } });
      if (error) {
        toast.error(error.message);
        return;
      }
      if (paymentIntent?.status === "succeeded") {
        const wallet = await paymentsApi.verifyStripe(paymentIntent.id, token);
        toast.success("Wallet topped up");
        onSuccess(wallet);
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not verify payment");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Top up wallet</DialogTitle>
          <DialogDescription>Add funds via card — processed by Paystack or Stripe.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Amount (NGN)</Label>
            <Input type="number" min="100" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="5000" disabled={provider === "stripe" && stripeReady} />
          </div>

          {provider !== "stripe" ? (
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button type="button" variant="outline" onClick={payWithPaystack} disabled={processing || !amountValid}>
                {processing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Pay with Paystack
              </Button>
              <Button type="button" variant="outline" onClick={startStripe} disabled={processing || !amountValid}>
                Pay with card (Stripe)
              </Button>
            </div>
          ) : (
            <div className="space-y-3 pt-1">
              <div ref={cardElRef} className="rounded-lg border p-3" />
              {!stripeReady ? <p className="text-xs text-muted-foreground">Loading card form…</p> : null}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={processing}>Cancel</Button>
          {provider === "stripe" ? (
            <Button onClick={confirmStripe} disabled={processing || !stripeReady}>
              {processing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Pay ₦{amount || "0"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
